import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { 
  FileText, 
  Plus,
  ChevronRight,
  Calendar,
  Clock,
  MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/StatusBadge';
import { toast } from 'sonner';
import { formatHoursDecimal } from '../lib/hoursFormat';

export const ReportsListPage = () => {
  const navigate = useNavigate();
  const { get } = useApi();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReports = async () => {
    try {
      const result = await get('/daily-reports');
      if (result.success) {
        setReports(result.data);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const createNewReport = () => {
    navigate('/reports/new');
  };

  if (loading) {
    return (
      <div className="page-wrapper p-4" data-testid="reports-loading">
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-slate-800 rounded-xl p-4 animate-pulse">
              <div className="h-5 bg-slate-700 rounded w-1/3 mb-2" />
              <div className="h-4 bg-slate-700 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper" data-testid="reports-list-page">
      {/* Header */}
      <div className="app-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="text-amber-500" size={28} />
            <div>
              <h1 className="text-2xl text-white">Rapports Journaliers</h1>
              <p className="text-slate-400 text-sm">{reports.length} rapport(s)</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Create New Button */}
        <Button
          onClick={createNewReport}
          className="w-full btn-large btn-primary-glow mb-6"
          data-testid="new-report-btn"
        >
          <Plus size={24} className="mr-2" />
          Nouveau Rapport
        </Button>

        {/* Reports List */}
        {reports.length === 0 ? (
          <div className="empty-state" data-testid="no-reports">
            <div className="empty-state-icon">
              <FileText className="text-slate-600" size={40} />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Aucun rapport</h3>
            <p className="text-slate-400">Créez votre premier rapport journalier</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map(report => (
              <button
                key={report.id}
                onClick={() => navigate(`/reports/${report.id}`)}
                className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-left hover:border-slate-700 transition-colors"
                data-testid={`report-item-${report.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-amber-500 font-bold">{report.report_number}</span>
                  <StatusBadge status={report.status} />
                </div>
                
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {report.date}
                  </span>
                  {report.stops?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <MapPin size={14} />
                      {report.stops.length} arrêt(s)
                    </span>
                  )}
                  {report.total_hours && (
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {formatHoursDecimal(report.total_hours)}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center justify-end mt-2">
                  <ChevronRight className="text-slate-600" size={20} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsListPage;
